import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { localS3Overrides } from "../../services/localS3";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  X,
  Send,
  Loader,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  FolderPlus,
  Edit2,
  FileWarning,
  ClipboardCheck,
  Download,
  Trash2,
} from "lucide-react";
import { generateClient } from "aws-amplify/api";
import { graphqlClient } from "../../services/graphqlClient";
import { buildSubmitProcessingPayload } from "../../services/apiPayloadBuilder";
import * as mutations from "../../graphql/mutations";
import {
  WorkflowJob,
  JobType,
  StepStatus,
  Loan,
  File as FileType,
  Document,
  LoanStatus,
  FileStatus,
  UploadCredentials,
  SubmitDocumentsForProcessingInput,
} from "../../graphql/types";
import { fetchAuthSession } from "aws-amplify/auth";
import { ConfirmModal, InfoModal } from "../Modal";

const client = generateClient();

const isPreviewMode =
  import.meta.env.VITE_PREVIEW === "true" ||
  !import.meta.env.VITE_COGNITO_USER_POOL_ID;

// Supported file formats - must match backend SQSMessageParser.supported_formats
const SUPPORTED_FILE_EXTENSIONS = new Set([
  "pdf",
  "png",
  "jpg",
  "jpeg",
  "tiff",
  "tif",
  "bmp",
  "gif",
]);
const SUPPORTED_FILE_TYPES_DISPLAY = "PDF, PNG, JPG, JPEG, TIFF, BMP, GIF";
const ACCEPTED_FILE_TYPES = ".pdf,.png,.jpg,.jpeg,.tiff,.tif,.bmp,.gif";

/**
 * Validate if a file has a supported format
 * @param filename - Name of the file to validate
 * @returns Object with isValid boolean and optional error message
 */
const validateFileFormat = (
  filename: string,
): { isValid: boolean; error?: string } => {
  if (!filename || !filename.includes(".")) {
    return { isValid: false, error: "File has no extension" };
  }
  const extension = filename.split(".").pop()?.toLowerCase();
  if (!extension || !SUPPORTED_FILE_EXTENSIONS.has(extension)) {
    return {
      isValid: false,
      error: `Unsupported format (.${extension}). Supported: ${SUPPORTED_FILE_TYPES_DISPLAY}`,
    };
  }
  return { isValid: true };
};

/**
 * Normalize a documentProcessingStatus value to uppercase for comparison.
 * The field carries the DD/BA StepStatus enum (already uppercase) for DD/BA
 * jobs and the lowercase title-search domain (pending | in_progress |
 * completed | not_found | failed | blocked) for TR jobs; uppercasing maps both
 * onto the StepStatus spellings (and "PENDING" for the TR not-started state).
 */
const normalizeStepStatus = (status: string | null | undefined): string =>
  (status ?? "").toString().toUpperCase();

/**
 * Count the files in a loan that have finished processing (reached a terminal,
 * non-failed state). This mirrors how the backend's post_bda_processing computes
 * processed_file_count, but derives it from the live file list so the displayed
 * "Processed Docs" count stays correct after a file is deleted — the deleteFile
 * mutation removes the file without decrementing the stored loan.processedFileCount.
 */
export const countProcessedFiles = (files: FileType[]): number =>
  files.filter(
    (f) =>
      f.status === FileStatus.REVIEW_REQUIRED ||
      f.status === FileStatus.REVIEW_COMPLETED,
  ).length;

/**
 * Total number of persisted files across every loan in a job, given the list of
 * each loan's files. Used after a delete to detect when the job has become
 * fileless (every file removed) so the workflow can be reset to a clean state.
 */
export const countTotalFiles = (perLoanFiles: FileType[][]): number =>
  perLoanFiles.reduce((total, files) => total + files.length, 0);

/**
 * Given a filename that already exists in the loan, return the next available
 * auto-incremented name: `{base}_{n}{ext}` where n starts at 1 and increments
 * until no collision with existingNames is found.
 * e.g. "download.jpeg" → "download_1.jpeg", then "download_2.jpeg", etc.
 */
export const getAutoIncrementName = (
  filename: string,
  existingNames: Set<string>,
): string => {
  const lastDot = filename.lastIndexOf(".");
  const base = lastDot >= 0 ? filename.slice(0, lastDot) : filename;
  const ext = lastDot >= 0 ? filename.slice(lastDot) : "";
  let n = 1;
  while (existingNames.has(`${base}_${n}${ext}`)) {
    n++;
  }
  return `${base}_${n}${ext}`;
};

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  status: "uploading" | "completed" | "error" | "rejected";
  progress: number;
  error?: string;
}

type LoanUploads = Map<string, UploadedFile[]>;

interface AddLoansState {
  step: "count" | "ids";
  count: number;
  autoGenerate: boolean;
  loanIds: string[];
}

/**
 * DocumentProcessingStep - First step in the workflow
 * Wraps existing DocIngestionJob functionality with workflow integration
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 13.4, 14.3
 */
interface DocumentProcessingStepProps {
  job: WorkflowJob;
  jobType: JobType;
  onStepComplete: () => void;
  onJobUpdate?: (updatedJob: WorkflowJob) => void;
  uploadCredentials?: UploadCredentials | null;
  uploadBucket?: string;
  /**
   * Title Search: open the file in the Workspace Review mode instead of the
   * legacy review page. When set, "Review data" is also shown for TR.
   */
  onReviewFile?: (file: FileType, documents: Document[]) => void;
  /**
   * Title Search: called when a file has finished processing and has documents
   * to review, so the page can move to Review mode without a reload.
   */
  onReviewReady?: () => void;
}

const DocumentProcessingStep: React.FC<DocumentProcessingStepProps> = ({
  job,
  jobType,
  onStepComplete,
  onJobUpdate,
  uploadCredentials: initialUploadCredentials,
  uploadBucket: initialUploadBucket,
  onReviewFile,
  onReviewReady,
}) => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [expandedLoans, setExpandedLoans] = useState<Set<string>>(new Set());
  const [uploadCredentials] = useState<UploadCredentials | null>(
    initialUploadCredentials || null,
  );
  const [uploadBucket] = useState<string | undefined>(initialUploadBucket);
  const [loanUploads, setLoanUploads] = useState<LoanUploads>(new Map());
  const [isDragOver, setIsDragOver] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loanFiles, setLoanFiles] = useState<Map<string, FileType[]>>(
    new Map(),
  );
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [fileDocuments, setFileDocuments] = useState<Map<string, Document[]>>(
    new Map(),
  );
  const [resubmitting, setResubmitting] = useState(false);
  const [confirmResubmit, setConfirmResubmit] = useState(false);
  const [infoModal, setInfoModal] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [addLoansModal, setAddLoansModal] = useState<AddLoansState | null>(
    null,
  );
  const [addingLoans, setAddingLoans] = useState(false);
  const [editingLoan, setEditingLoan] = useState<Loan | null>(null);
  const [editLoanNumber, setEditLoanNumber] = useState("");
  const [editBorrowerName, setEditBorrowerName] = useState("");
  const [editPropertyAddress, setEditPropertyAddress] = useState("");
  const [savingLoanInfo, setSavingLoanInfo] = useState(false);
  const [currentJob, setCurrentJob] = useState<WorkflowJob>(job);
  // Mirror of currentJob so async callbacks (loadJobData) can read the latest
  // value without adding currentJob to their dependency arrays.
  const currentJobRef = React.useRef(currentJob);
  useEffect(() => {
    currentJobRef.current = currentJob;
  }, [currentJob]);
  const [reviewingFileId, setReviewingFileId] = useState<string | null>(null);
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [downloadingBookmarkLoanId, setDownloadingBookmarkLoanId] = useState<
    string | null
  >(null);
  // Add/delete document management state
  const [confirmDeleteFile, setConfirmDeleteFile] = useState<{
    file: FileType;
    loanId: string;
  } | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<{
    doc: Document;
    fileId: string;
    loanId: string;
  } | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [processingAdditional, setProcessingAdditional] = useState(false);
  const [conflictModal, setConflictModal] = useState<{
    loanId: string;
    conflictingFiles: Array<{ file: File; proposedName: string }>;
    nonConflictingFiles: File[];
  } | null>(null);
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

  // Determine step state.
  // documentProcessingStatus carries two domains depending on job type:
  //   - DD/BA: the StepStatus enum (NOT_STARTED | IN_PROGRESS | COMPLETED | FAILED)
  //   - TR:    the lowercase title-search domain (pending | in_progress |
  //            completed | not_found | failed | blocked)
  // Normalize to uppercase so both map onto the same step-state flags.
  // (pending/empty => not started; blocked => treated as failed.)
  const normalizedDpStatus = normalizeStepStatus(
    currentJob.documentProcessingStatus,
  );
  const isStepNotStarted =
    normalizedDpStatus === StepStatus.NOT_STARTED ||
    normalizedDpStatus === "PENDING" ||
    normalizedDpStatus === "";
  const isStepInProgress = normalizedDpStatus === StepStatus.IN_PROGRESS;
  const isStepCompleted = normalizedDpStatus === StepStatus.COMPLETED;
  const isStepFailed =
    normalizedDpStatus === StepStatus.FAILED ||
    normalizedDpStatus === "BLOCKED";

  // Documents can be uploaded/added and deleted before processing starts, while
  // processing is in progress, and after it completes. (FAILED uses the Retry
  // path instead.) This lets users manage a loan's documents at any point.
  const canManageDocs = isStepNotStarted || isStepInProgress || isStepCompleted;

  // Load job data including loans, files, and documents
  const loadJobData = useCallback(async () => {
    if (!job.jobId || isPreviewMode) return;
    try {
      const session = await fetchAuthSession();
      const tenantId =
        (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
        "default-tenant";

      // Fetch updated job data to get latest status
      const updatedJobData = await graphqlClient.queries.getJob(
        job.jobId,
        tenantId,
      );
      if (updatedJobData) {
        // Compare against the latest job (via ref) and apply both the local and
        // parent updates from this async callback. Do NOT call onJobUpdate from
        // inside a setCurrentJob updater — React runs updaters during render, and
        // updating the parent (WorkflowJobPage) then triggers the "Cannot update a
        // component while rendering a different component" warning.
        const prevJob = currentJobRef.current;
        const hasChanged =
          updatedJobData.status !== prevJob.status ||
          updatedJobData.documentProcessingStatus !==
            prevJob.documentProcessingStatus ||
          updatedJobData.documentVerificationStatus !==
            prevJob.documentVerificationStatus ||
          updatedJobData.investorGuidelineReviewStatus !==
            prevJob.investorGuidelineReviewStatus ||
          updatedJobData.processedLoanCount !== prevJob.processedLoanCount ||
          updatedJobData.loanCount !== prevJob.loanCount ||
          updatedJobData.verifiedDocumentCount !==
            prevJob.verifiedDocumentCount ||
          updatedJobData.guidelineCheckCount !== prevJob.guidelineCheckCount;

        if (hasChanged) {
          setCurrentJob(updatedJobData);
          onJobUpdate?.(updatedJobData);
        }
      }

      // Load loans for this job
      const loansData = await graphqlClient.queries.listLoansByJob(
        job.jobId,
        tenantId,
      );
      setLoans(loansData.items);

      // Load files and documents for each loan
      for (const loan of loansData.items) {
        const filesData = await graphqlClient.queries.listFilesByLoan(
          loan.loanId,
          job.jobId,
          tenantId,
        );
        setLoanFiles((prev) => new Map(prev).set(loan.loanId, filesData.items));
        for (const file of filesData.items) {
          const docs = await graphqlClient.queries.listDocumentsByFile(
            file.fileId,
            loan.loanId,
            job.jobId,
            tenantId,
          );
          setFileDocuments((prev) =>
            new Map(prev).set(file.fileId, docs.items),
          );
        }
      }
    } catch (err) {
      console.error("Failed to load job data:", err);
      setError("Failed to load job data");
    }
  }, [job.jobId, onJobUpdate]);

  useEffect(() => {
    loadJobData();
  }, [loadJobData]);

  // A file that has finished processing and has documents is ready to review.
  const hasReviewableFile = [...loanFiles.values()]
    .flat()
    .some(
      (f) =>
        f.status === FileStatus.REVIEW_REQUIRED &&
        (fileDocuments.get(f.fileId) || []).length > 0,
    );
  useEffect(() => {
    if (hasReviewableFile) onReviewReady?.();
    // Only react to a file becoming reviewable, not to a new callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasReviewableFile]);

  // Poll for updates when step is IN_PROGRESS
  useEffect(() => {
    if (!isStepInProgress) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    pollingIntervalRef.current = setInterval(async () => {
      await loadJobData();
    }, 3000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isStepInProgress, loadJobData]);

  // Update current job when prop changes, but only if the prop has newer data
  useEffect(() => {
    if (job.documentProcessingStatus !== currentJob.documentProcessingStatus) {
      const propStatus = normalizeStepStatus(job.documentProcessingStatus);
      const localStatus = normalizeStepStatus(
        currentJob.documentProcessingStatus,
      );
      const propIsMoreRecent =
        propStatus === StepStatus.IN_PROGRESS ||
        propStatus === StepStatus.COMPLETED ||
        propStatus === StepStatus.FAILED;

      const localIsOptimistic =
        localStatus === StepStatus.IN_PROGRESS &&
        (propStatus === StepStatus.NOT_STARTED || propStatus === "PENDING");

      if (propIsMoreRecent || !localIsOptimistic) {
        setCurrentJob(job);
      }
    }
  }, [job, currentJob.documentProcessingStatus]);

  // Note: onStepComplete is intentionally NOT called automatically when the step completes.
  // The user must explicitly click "Next: Document Verification" to advance.

  // Submit readiness check - only count completed (valid) uploads, exclude rejected files
  const allLoansHaveValidFiles =
    loans.length > 0 &&
    loans.every((loan) => {
      const uploads = loanUploads.get(loan.loanId) || [];
      return uploads.some((f) => f.status === "completed");
    });
  const anyUploading = [...loanUploads.values()]
    .flat()
    .some((f) => f.status === "uploading");
  const canSubmit =
    (allLoansHaveValidFiles ||
      loans.some((l) => (loanFiles.get(l.loanId) || []).length > 0)) &&
    !anyUploading &&
    !submitting &&
    isStepNotStarted;

  // Add Loans modal handlers
  const openAddLoansModal = () => {
    setAddLoansModal({
      step: "count",
      count: 1,
      autoGenerate: true,
      loanIds: [],
    });
  };

  const handleAddLoansCountSubmit = () => {
    if (!addLoansModal) return;
    if (addLoansModal.autoGenerate) {
      handleConfirmAddLoans(addLoansModal.count, true, []);
    } else {
      setAddLoansModal({
        ...addLoansModal,
        step: "ids",
        loanIds: Array(addLoansModal.count).fill(""),
      });
    }
  };

  /**
   * Requirement 6.2: Allow users to add loans to the job
   * Updates job's loanCount when loans are added
   */
  const handleConfirmAddLoans = async (
    count: number,
    autoGenerate: boolean,
    loanIds: string[],
  ) => {
    setAddLoansModal(null);
    setAddingLoans(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const tenantId =
        (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
        "default-tenant";
      const username =
        (session.tokens?.idToken?.payload["cognito:username"] as string) ||
        "unknown";
      const userId =
        (session.tokens?.idToken?.payload["cognito:username"] as string) ||
        (session.tokens?.idToken?.payload["sub"] as string) ||
        "unknown";
      const cognitoSub =
        (session.tokens?.idToken?.payload["sub"] as string) || "unknown";

      const newLoans: Loan[] = [];
      for (let i = 0; i < count; i++) {
        const loanId = autoGenerate
          ? `loan-${Date.now()}-${i}`
          : loanIds[i].trim();
        const loanNumber = autoGenerate
          ? `Loan ${loans.length + i + 1}`
          : loanIds[i].trim();

        const loan = await graphqlClient.mutations.createLoan({
          loanId,
          jobId: job.jobId,
          tenantId,
          username,
          cognitoSub,
          loanNumber,
        });
        newLoans.push(loan);
      }

      setLoans((prev) => [...prev, ...newLoans]);
      if (newLoans.length > 0) {
        setExpandedLoans((prev) => new Set([...prev, newLoans[0].loanId]));
      }
    } catch (err) {
      console.error("Failed to add loans:", err);
      setError("Failed to add loans. Please try again.");
    } finally {
      setAddingLoans(false);
    }
  };

  /**
   * Core S3 upload logic. Receives already-validated (and conflict-resolved)
   * File objects and uploads them to S3 for the given loan.
   */
  const uploadValidatedFiles = useCallback(
    async (validFiles: File[], loanId: string) => {
      const newFiles: UploadedFile[] = validFiles.map((file) => ({
        id: Math.random().toString(36).substring(2, 11),
        name: file.name,
        size: file.size,
        status: "uploading" as const,
        progress: 0,
      }));

      setLoanUploads((prev) => {
        const next = new Map(prev);
        next.set(loanId, [...(next.get(loanId) || []), ...newFiles]);
        return next;
      });

      if (isPreviewMode) {
        for (const fileRecord of newFiles) {
          for (let p = 0; p <= 100; p += 20) {
            await new Promise((r) => setTimeout(r, 80));
            setLoanUploads((prev) => {
              const next = new Map(prev);
              next.set(
                loanId,
                (next.get(loanId) || []).map((f) =>
                  f.id === fileRecord.id ? { ...f, progress: p } : f,
                ),
              );
              return next;
            });
          }
          setLoanUploads((prev) => {
            const next = new Map(prev);
            next.set(
              loanId,
              (next.get(loanId) || []).map((f) =>
                f.id === fileRecord.id
                  ? { ...f, status: "completed", progress: 100 }
                  : f,
              ),
            );
            return next;
          });
        }
        return;
      }

      if (!uploadBucket) {
        setError("Upload bucket not configured. Please refresh the page.");
        setLoanUploads((prev) => {
          const next = new Map(prev);
          next.set(
            loanId,
            (next.get(loanId) || []).map((f) =>
              newFiles.find((nf) => nf.id === f.id)
                ? { ...f, status: "error", error: "No upload config" }
                : f,
            ),
          );
          return next;
        });
        return;
      }

      const session = await fetchAuthSession();
      const creds = session.credentials!;

      // Derive upload prefix from job's uploadLocation (source of truth)
      let uploadPrefix = "";
      if (job.uploadLocation) {
        uploadPrefix = job.uploadLocation.replace(/^s3:\/\/[^/]+\//, "");
      } else {
        const userId =
          (session.tokens?.idToken?.payload["cognito:username"] as string) ||
          "unknown";
        uploadPrefix = `default-tenant/${userId}/${job.jobId}/`;
      }

      const s3Client = new S3Client({
        region: import.meta.env.VITE_AWS_REGION || "us-west-2",
        credentials: {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          sessionToken: creds.sessionToken,
        },
        requestChecksumCalculation: "WHEN_REQUIRED",
        responseChecksumValidation: "WHEN_REQUIRED",
        ...localS3Overrides(),
      });

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const fileRecord = newFiles[i];
        try {
          const key = `${uploadPrefix}${loanId}/${file.name}`;
          const arrayBuffer = await file.arrayBuffer();
          await s3Client.send(
            new PutObjectCommand({
              Bucket: uploadBucket,
              Key: key,
              Body: new Uint8Array(arrayBuffer),
              ContentType: file.type,
            }),
          );
          setLoanUploads((prev) => {
            const next = new Map(prev);
            next.set(
              loanId,
              (next.get(loanId) || []).map((f) =>
                f.id === fileRecord.id
                  ? { ...f, status: "completed", progress: 100 }
                  : f,
              ),
            );
            return next;
          });
        } catch (uploadErr) {
          setLoanUploads((prev) => {
            const next = new Map(prev);
            next.set(
              loanId,
              (next.get(loanId) || []).map((f) =>
                f.id === fileRecord.id
                  ? {
                      ...f,
                      status: "error",
                      error:
                        uploadErr instanceof Error
                          ? uploadErr.message
                          : "Upload failed",
                    }
                  : f,
              ),
            );
            return next;
          });
        }
      }
    },
    [uploadBucket, job.jobId, job.uploadLocation],
  );

  /**
   * Requirement 6.3: Allow users to upload documents for each loan.
   * Validates file formats, detects filename conflicts with already-persisted
   * files, and either shows the conflict modal or proceeds with the upload.
   */
  const handleFiles = useCallback(
    async (fileList: File[], loanId: string) => {
      if (!loanId) {
        setError("Please select a loan before uploading files");
        return;
      }

      // Validate file formats and separate valid from invalid files
      const validFiles: File[] = [];
      const rejectedFiles: UploadedFile[] = [];

      for (const file of fileList) {
        const validation = validateFileFormat(file.name);
        if (validation.isValid) {
          validFiles.push(file);
        } else {
          rejectedFiles.push({
            id: Math.random().toString(36).substring(2, 11),
            name: file.name,
            size: file.size,
            status: "rejected",
            progress: 0,
            error: validation.error,
          });
        }
      }

      // Add rejected files to the upload list to show errors
      if (rejectedFiles.length > 0) {
        setLoanUploads((prev) => {
          const next = new Map(prev);
          next.set(loanId, [...(next.get(loanId) || []), ...rejectedFiles]);
          return next;
        });
      }

      // If no valid files, return early
      if (validFiles.length === 0) {
        if (rejectedFiles.length > 0) {
          setError(
            `${rejectedFiles.length} file(s) rejected. Supported formats: ${SUPPORTED_FILE_TYPES_DISPLAY}`,
          );
        }
        return;
      }

      // Check for filename conflicts with already-persisted files for this loan.
      // Build a running "taken names" set so auto-incremented proposals within a
      // single batch also avoid collisions with each other.
      const persistedFilenames = new Set(
        (loanFiles.get(loanId) || []).map((f) => f.filename),
      );
      const takenNames = new Set(persistedFilenames);

      const conflictingFiles: Array<{ file: File; proposedName: string }> = [];
      const nonConflictingFiles: File[] = [];

      for (const file of validFiles) {
        if (persistedFilenames.has(file.name)) {
          const proposedName = getAutoIncrementName(file.name, takenNames);
          takenNames.add(proposedName);
          conflictingFiles.push({ file, proposedName });
        } else {
          takenNames.add(file.name);
          nonConflictingFiles.push(file);
        }
      }

      if (conflictingFiles.length > 0) {
        setConflictModal({ loanId, conflictingFiles, nonConflictingFiles });
        return;
      }

      await uploadValidatedFiles(validFiles, loanId);
    },
    [uploadValidatedFiles, loanFiles],
  );

  const handleConflictReplace = async () => {
    if (!conflictModal) return;
    const { loanId, conflictingFiles, nonConflictingFiles } = conflictModal;
    setConflictModal(null);
    setError(null);
    try {
      if (!isPreviewMode) {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
          "default-tenant";

        // Delete the existing persisted file(s) (record + extracted documents)
        // that the uploads collide with, so "Replace" truly replaces rather than
        // leaving stale results behind. The new same-name upload below overwrites
        // the S3 object; its newer LastModified lets the time-aware tombstone
        // reprocess it. Dedupe by fileId in case multiple uploads share a name.
        const persisted = loanFiles.get(loanId) || [];
        const toDelete = new Map<string, FileType>();
        for (const { file } of conflictingFiles) {
          const existing = persisted.find((f) => f.filename === file.name);
          if (existing) toDelete.set(existing.fileId, existing);
        }
        for (const existing of toDelete.values()) {
          await graphqlClient.mutations.deleteFile(
            existing.fileId,
            loanId,
            job.jobId,
            tenantId,
            existing.s3Key,
          );
        }
        // Refresh so the replaced file and its old results disappear before the
        // new upload is staged.
        if (toDelete.size > 0) await loadJobData();
      }
    } catch (err) {
      console.error("Failed to remove existing file before replace:", err);
      setError("Failed to replace the existing file. Please try again.");
      return;
    }

    await uploadValidatedFiles(
      [...conflictingFiles.map((c) => c.file), ...nonConflictingFiles],
      loanId,
    );
  };

  const handleConflictUploadNew = () => {
    if (!conflictModal) return;
    const { loanId, conflictingFiles, nonConflictingFiles } = conflictModal;
    setConflictModal(null);
    const renamedFiles = conflictingFiles.map(
      (c) => new File([c.file], c.proposedName, { type: c.file.type }),
    );
    uploadValidatedFiles([...renamedFiles, ...nonConflictingFiles], loanId);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent, loanId: string) => {
      e.preventDefault();
      setIsDragOver(null);
      handleFiles(Array.from(e.dataTransfer.files), loanId);
    },
    [handleFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, loanId: string) => {
      if (e.target.files) handleFiles(Array.from(e.target.files), loanId);
    },
    [handleFiles],
  );

  const removeFile = (loanId: string, fileId: string) => {
    setLoanUploads((prev) => {
      const next = new Map(prev);
      next.set(
        loanId,
        (next.get(loanId) || []).filter((f) => f.id !== fileId),
      );
      return next;
    });
  };

  /**
   * Requirement 6.4, 6.5, 13.4: Submit job for processing with jobType
   * Uses ApiPayloadBuilder to include jobType in submission payload
   * Transitions documentProcessingStatus to IN_PROGRESS on submit
   * Displays real-time status updates during processing
   */
  const handleSubmitJob = async () => {
    if (!job.jobId) {
      setError("Job ID not available");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isPreviewMode) {
        await new Promise((r) => setTimeout(r, 1000));
        const optimisticJob = {
          ...job,
          documentProcessingStatus: StepStatus.IN_PROGRESS,
          status: "IN_PROGRESS" as any,
        };
        setCurrentJob(optimisticJob);
        onJobUpdate?.(optimisticJob);
      } else {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
          "default-tenant";

        const payload: SubmitDocumentsForProcessingInput =
          buildSubmitProcessingPayload(job.jobId, tenantId, jobType);

        const result = await client.graphql({
          query: mutations.submitDocumentsForProcessing,
          variables: { input: payload },
        });

        const updatedJob = (result as any).data?.submitDocumentsForProcessing;

        if (updatedJob && updatedJob.documentProcessingStatus) {
          setCurrentJob(updatedJob);
          onJobUpdate?.(updatedJob);
        } else {
          const optimisticJob = {
            ...job,
            documentProcessingStatus: StepStatus.IN_PROGRESS,
            status: "IN_PROGRESS" as any,
          };
          setCurrentJob(optimisticJob);
          onJobUpdate?.(optimisticJob);
        }

        // Submit to the document processing queue
        await client.graphql({
          query: mutations.submitJob,
          variables: { input: { jobId: job.jobId } },
        });
      }
      setLoanUploads(new Map());
      await loadJobData();
    } catch (err) {
      console.error("Failed to submit job:", err);
      setError("Failed to submit job for processing");
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Requirement 6.7: Handle processing failures with retry option
   * Mark step as FAILED on processing errors with retry option
   */
  const handleResubmitJob = async () => {
    if (!isStepFailed) return;
    setConfirmResubmit(true);
  };

  const handleResubmitConfirm = async () => {
    setConfirmResubmit(false);
    setResubmitting(true);
    setError(null);
    try {
      if (isPreviewMode) {
        await new Promise((r) => setTimeout(r, 2000));
        const optimisticJob = {
          ...currentJob,
          documentProcessingStatus: StepStatus.IN_PROGRESS,
        };
        setCurrentJob(optimisticJob);
        onJobUpdate?.(optimisticJob);
      } else {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
          "default-tenant";

        // Resubmit with jobType
        const payload: SubmitDocumentsForProcessingInput =
          buildSubmitProcessingPayload(job.jobId, tenantId, jobType);

        const result = await client.graphql({
          query: mutations.submitDocumentsForProcessing,
          variables: { input: payload },
        });

        const updatedJob = (result as any).data?.submitDocumentsForProcessing;
        if (updatedJob) {
          setCurrentJob(updatedJob);
          onJobUpdate?.(updatedJob);
        } else {
          // If mutation didn't return updated job, update local state optimistically
          const optimisticJob = {
            ...job,
            documentProcessingStatus: StepStatus.IN_PROGRESS,
            status: "IN_PROGRESS" as any,
          };
          setCurrentJob(optimisticJob);
          onJobUpdate?.(optimisticJob);
        }

        // Also resubmit to the document processing queue
        await client.graphql({
          query: mutations.submitJob,
          variables: { input: { jobId: job.jobId, isResubmission: true } },
        });

        await loadJobData();
      }
    } catch (err) {
      setError("Failed to resubmit job. Please try again.");
    } finally {
      setResubmitting(false);
    }
  };

  // Whether there are freshly uploaded (S3) files staged but not yet submitted
  // for processing. Used to surface the "Submit Added Documents" action when the
  // step is already in progress or completed.
  const hasPendingUploads = [...loanUploads.values()]
    .flat()
    .some((f) => f.status === "completed");

  const canSubmitAdditional =
    (isStepInProgress || isStepCompleted) &&
    hasPendingUploads &&
    !anyUploading &&
    !processingAdditional;

  /**
   * Submit additional documents that were uploaded after the initial submit
   * (while IN_PROGRESS or after COMPLETED). Re-triggers the processing pipeline
   * to pick up the newly uploaded S3 objects. Does not advance the workflow step.
   * Requirements: 1 (add while processing), 2 (add after completion), 5 (refresh).
   */
  const handleProcessAdditionalDocuments = async () => {
    if (!job.jobId || !canSubmitAdditional) return;
    setProcessingAdditional(true);
    setError(null);
    try {
      if (isPreviewMode) {
        await new Promise((r) => setTimeout(r, 800));
      } else {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
          "default-tenant";

        const payload: SubmitDocumentsForProcessingInput =
          buildSubmitProcessingPayload(job.jobId, tenantId, jobType);

        const result = await client.graphql({
          query: mutations.submitDocumentsForProcessing,
          variables: { input: payload },
        });
        const updatedJob = (result as any).data?.submitDocumentsForProcessing;
        if (updatedJob) {
          setCurrentJob(updatedJob);
          onJobUpdate?.(updatedJob);
        }

        await client.graphql({
          query: mutations.submitJob,
          variables: { input: { jobId: job.jobId, isResubmission: true } },
        });
      }
      setLoanUploads(new Map());
      await loadJobData();
    } catch (err) {
      console.error("Failed to submit added documents:", err);
      setError("Failed to submit added documents for processing.");
    } finally {
      setProcessingAdditional(false);
    }
  };

  /**
   * After a delete, check whether the whole job has become fileless (every file
   * removed). If so, reset it to a clean, recoverable state: each (now empty)
   * loan back to CREATED and the document-processing step back to NOT_STARTED.
   * This clears the "stuck IN_PROGRESS" state and the perpetual "Extracting
   * documents…" skeleton so the user can simply upload again.
   *
   * Delete-triggered (not run from polling) on purpose: right after a submit
   * there is a brief window where the step is IN_PROGRESS but file records do
   * not exist yet, and resetting then would be wrong.
   */
  const maybeResetEmptiedJob = async (tenantId: string): Promise<boolean> => {
    if (isPreviewMode) return false;

    // Fresh authoritative fetch of the current loans/files (don't rely on state,
    // which updates asynchronously).
    const loansData = await graphqlClient.queries.listLoansByJob(
      job.jobId,
      tenantId,
    );
    const perLoanFiles: FileType[][] = [];
    for (const loan of loansData.items) {
      const filesData = await graphqlClient.queries.listFilesByLoan(
        loan.loanId,
        job.jobId,
        tenantId,
      );
      perLoanFiles.push(filesData.items);
    }

    if (countTotalFiles(perLoanFiles) > 0) return false;
    if (normalizeStepStatus(currentJobRef.current.documentProcessingStatus) ===
        StepStatus.NOT_STARTED) {
      return false;
    }

    // Reset each (empty) loan back to its initial state so the skeleton stops.
    // Tolerate per-loan failures so they can't block the critical step reset
    // below.
    for (const loan of loansData.items) {
      try {
        await client.graphql({
          query: mutations.updateLoanStatus,
          variables: {
            loanId: loan.loanId,
            jobId: job.jobId,
            tenantId,
            status: LoanStatus.CREATED,
            processedFileCount: 0,
            failedFileCount: 0,
          },
        });
      } catch (loanErr) {
        console.error(`Failed to reset loan ${loan.loanId}:`, loanErr);
      }
    }

    // Reset the document-processing step to NOT_STARTED.
    const result = await client.graphql({
      query: mutations.updateStepStatus,
      variables: {
        input: {
          jobId: job.jobId,
          tenantId,
          jobType,
          step: "DOCUMENT_PROCESSING",
          status: StepStatus.NOT_STARTED,
        },
      },
    });
    const updatedJob = (result as any).data?.updateStepStatus;
    if (updatedJob) {
      setCurrentJob(updatedJob);
      onJobUpdate?.(updatedJob);
    }
    return true;
  };

  /**
   * Delete a persisted file (and its nested documents) from a loan, then refresh.
   * Requirements: 3 (delete), 4 (confirm — via ConfirmModal), 5 (refresh), 7 (scoping).
   */
  const handleConfirmDeleteFile = async () => {
    if (!confirmDeleteFile) return;
    const { file, loanId } = confirmDeleteFile;
    setConfirmDeleteFile(null);
    setDeletingFileId(file.fileId);
    setError(null);
    try {
      if (!isPreviewMode) {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
          "default-tenant";
        await graphqlClient.mutations.deleteFile(
          file.fileId,
          loanId,
          job.jobId,
          tenantId,
          file.s3Key,
        );
        // If that was the last file in the job, reset it to a clean state.
        // Best-effort: the delete already succeeded, so a reset hiccup must not
        // surface as a delete failure (the backend self-heal is the backstop).
        try {
          await maybeResetEmptiedJob(tenantId);
        } catch (resetErr) {
          console.error("Failed to reset emptied job after delete:", resetErr);
        }
      }
      await loadJobData();
    } catch (err) {
      console.error("Failed to delete file:", err);
      setError("Failed to delete file. Please try again.");
    } finally {
      setDeletingFileId(null);
    }
  };

  /**
   * Delete a single extracted document from a file, then refresh.
   * Requirements: 3 (delete), 4 (confirm), 5 (refresh), 7 (scoping).
   */
  const handleConfirmDeleteDocument = async () => {
    if (!confirmDeleteDoc) return;
    const { doc, fileId, loanId } = confirmDeleteDoc;
    setConfirmDeleteDoc(null);
    setDeletingDocId(doc.documentId);
    setError(null);
    try {
      if (!isPreviewMode) {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
          "default-tenant";
        await graphqlClient.mutations.deleteDocument(
          doc.documentId,
          fileId,
          loanId,
          job.jobId,
          tenantId,
        );
        // Deleting documents doesn't remove the file, but reset defensively in
        // case this leaves the job with no files at all. Best-effort (see above).
        try {
          await maybeResetEmptiedJob(tenantId);
        } catch (resetErr) {
          console.error("Failed to reset emptied job after delete:", resetErr);
        }
      }
      await loadJobData();
    } catch (err) {
      console.error("Failed to delete document:", err);
      setError("Failed to delete document. Please try again.");
    } finally {
      setDeletingDocId(null);
    }
  };

  // Edit loan handlers
  const handleEditLoan = (loan: Loan, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingLoan(loan);
    setEditLoanNumber(loan.loanNumber || "");
    setEditBorrowerName(loan.borrowerName || "");
    setEditPropertyAddress(loan.propertyAddress || "");
  };

  const handleSaveLoanInfo = async () => {
    if (!editingLoan) return;
    setSavingLoanInfo(true);
    setError(null);
    try {
      const session = await fetchAuthSession();
      const tenantId =
        (session.tokens?.idToken?.payload["custom:tenantId"] as string) ||
        "default-tenant";
      await graphqlClient.mutations.updateLoanInfo(
        editingLoan.loanId,
        editingLoan.jobId,
        tenantId,
        editLoanNumber || undefined,
        editBorrowerName || undefined,
        editPropertyAddress || undefined,
      );
      setLoans((prev) =>
        prev.map((l) =>
          l.loanId === editingLoan.loanId
            ? {
                ...l,
                loanNumber: editLoanNumber || l.loanNumber,
                borrowerName: editBorrowerName || l.borrowerName,
                propertyAddress: editPropertyAddress || l.propertyAddress,
              }
            : l,
        ),
      );
      setEditingLoan(null);
    } catch (err) {
      setError("Failed to update loan information.");
    } finally {
      setSavingLoanInfo(false);
    }
  };

  // Helper functions
  const toggleLoanExpansion = (loanId: string) => {
    setExpandedLoans((prev) => {
      const next = new Set(prev);
      next.has(loanId) ? next.delete(loanId) : next.add(loanId);
      return next;
    });
  };

  const toggleFileExpansion = (fileId: string) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      next.has(fileId) ? next.delete(fileId) : next.add(fileId);
      return next;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024,
      sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatStatus = (status: string) =>
    status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const getLoanStatusBadge = (status: LoanStatus) => {
    const base = "px-2 py-1 rounded text-xs font-medium";
    const map: Record<string, string> = {
      [LoanStatus.CREATED]: `${base} bg-gray-100 text-gray-800`,
      [LoanStatus.PROCESSING]: `${base} bg-blue-100 text-blue-800`,
      [LoanStatus.REVIEW_REQUIRED]: `${base} bg-yellow-100 text-yellow-800`,
      [LoanStatus.REVIEW_COMPLETED]: `${base} bg-green-100 text-green-800`,
    };
    return (
      map[status] ||
      `${base} bg-gray-100 text-gray-800`
    );
  };

  const getFileStatusBadge = (status: FileStatus) => {
    const base = "px-2 py-1 rounded text-xs font-medium";
    const map: Record<string, string> = {
      [FileStatus.CREATED]: `${base} bg-gray-100 text-gray-800`,
      [FileStatus.PROCESSING]: `${base} bg-blue-100 text-blue-800`,
      [FileStatus.REVIEW_REQUIRED]: `${base} bg-yellow-100 text-yellow-800`,
      [FileStatus.REVIEW_COMPLETED]: `${base} bg-green-100 text-green-800`,
    };
    return (
      map[status] ||
      `${base} bg-gray-100 text-gray-800`
    );
  };

  const getDocumentStatusBadge = (_status: string) => {
    return "px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800";
  };

  const handleOpenA2IReview = (reviewTaskARN: string) => {
    if (isPreviewMode) {
      setInfoModal({
        title: "Preview Mode",
        message: `Would open A2I review for ${reviewTaskARN}`,
      });
      return;
    }
    const region = import.meta.env.VITE_AWS_REGION || "us-east-1";
    const taskId = reviewTaskARN.split("/").pop();
    window.open(
      `https://${region}.console.aws.amazon.com/sagemaker/groundtruth?region=${region}#/labeling-workforces/private/tasks/${taskId}`,
      "_blank",
    );
  };

  const handleReviewFile = (file: FileType) => {
    const documents = fileDocuments.get(file.fileId) || [];
    if (documents.length === 0) return;

    if (onReviewFile) {
      onReviewFile(file, documents);
      return;
    }

    setReviewingFileId(file.fileId);
    navigate("/file-review", {
      state: {
        file,
        documents,
        uploadBucket: uploadBucket || import.meta.env.VITE_S3_DOCUMENTS_BUCKET,
        uploadCredentials,
      },
    });
  };

  async function handleOpenDocument(doc: Document): Promise<void> {
    setOpeningDocId(doc.documentId);
    try {
      const url = await graphqlClient.queries.getDocumentSplitUrl(
        doc.documentId,
        doc.jobId,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError("Failed to open document. Please try again.");
    } finally {
      setOpeningDocId(null);
    }
  }

  /**
   * Requirement 14.1, 14.2, 14.3: Download bookmarked PDF for a loan
   * Calls getLoanBookmarkedPdfUrl and opens the presigned URL in a new tab
   */
  async function handleDownloadBookmarkedPdf(loan: Loan): Promise<void> {
    setDownloadingBookmarkLoanId(loan.loanId);
    try {
      const url = await graphqlClient.queries.getLoanBookmarkedPdfUrl(
        job.jobId,
        loan.loanId,
      );
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError("Failed to download bookmarked PDF. Please try again.");
    } finally {
      setDownloadingBookmarkLoanId(null);
    }
  }

  // Render
  return (
    <div className="space-y-6">
      {/* Step Status Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Document Processing
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {isStepNotStarted &&
              (jobType === JobType.BA
                ? "Upload documents to begin processing."
                : "Add loans and upload documents to begin processing.")}
            {isStepInProgress &&
              "Documents are being processed. This page will update automatically."}
            {isStepCompleted &&
              "All documents have been processed successfully."}
            {isStepFailed &&
              "Document processing failed. You can retry the failed items."}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {isStepNotStarted && jobType !== JobType.BA && (
            <button
              onClick={openAddLoansModal}
              disabled={addingLoans}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {addingLoans ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <FolderPlus className="h-5 w-5 mr-2" />
                  Add Loans
                </>
              )}
            </button>
          )}
          {isStepFailed && (
            <button
              onClick={handleResubmitJob}
              disabled={resubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {resubmitting ? (
                <>
                  <Loader className="h-5 w-5 mr-2 animate-spin" />
                  Resubmitting...
                </>
              ) : (
                <>
                  <RefreshCw className="h-5 w-5 mr-2" />
                  Retry Processing
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-white">{error}</p>
          </div>
        </div>
      )}

      {/* Completed Banner */}
      {isStepCompleted && (
        <div className="border rounded-xl p-5 backdrop-blur-sm bg-green-50 border-green-200">
          <div className="flex items-start space-x-4">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-800">
                Document Processing Complete
              </p>
              <p className="mt-1 text-xs text-green-700">
                All {currentJob.loanCount} loan
                {currentJob.loanCount !== 1 ? "s" : ""} have been processed. You
                can now proceed to Document Verification.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {loans.length === 0 && !isPreviewMode && isStepNotStarted && (
        <div className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200 p-12 text-center">
          <FolderPlus className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No loans yet
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {jobType === JobType.BA
              ? "The loan for this job will be available shortly."
              : "Add loans to this job to start uploading files."}
          </p>
          {jobType !== JobType.BA && (
            <button
              onClick={openAddLoansModal}
              disabled={addingLoans}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
            >
              <FolderPlus className="h-5 w-5 mr-2" /> Add Loans
            </button>
          )}
        </div>
      )}

      {/* Loan List */}
      {loans.length > 0 && (
        <div className="space-y-6">
          {loans.map((loan) => {
            const isExpanded = expandedLoans.has(loan.loanId);
            const loanFilesData = loanFiles.get(loan.loanId) || [];
            // Derive from the live file list rather than the stored
            // loan.processedFileCount, which goes stale after a file delete
            // (deleteFile does not decrement it). See countProcessedFiles.
            const processedFileCount = countProcessedFiles(loanFilesData);
            const uploads = loanUploads.get(loan.loanId) || [];
            const validUploads = uploads.filter(
              (f) => f.status === "completed",
            );
            const hasUploadedFiles = validUploads.length > 0;

            return (
              <div
                key={loan.loanId}
                className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200"
              >
                {/* Loan Header */}
                <div
                  className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleLoanExpansion(loan.loanId)}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {loan.loanNumber || `Loan ${loan.loanId.slice(0, 8)}`}
                        </h3>
                        <span className={getLoanStatusBadge(loan.status)}>
                          {formatStatus(loan.status)}
                        </span>
                        {isStepNotStarted && (
                          <button
                            onClick={(e) => handleEditLoan(loan, e)}
                            className="text-gray-400 hover:text-blue-500 transition-colors"
                            title="Edit loan"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                        {isStepNotStarted && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${hasUploadedFiles ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                          >
                            {hasUploadedFiles
                              ? `${validUploads.length} file(s) ready`
                              : "No files yet"}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center space-x-6 text-sm text-gray-600">
                        <span>Files: {loanFilesData.length}</span>
                        <span>Processed Docs: {processedFileCount}</span>
                        {loan.borrowerName && (
                          <span>Borrower: {loan.borrowerName}</span>
                        )}
                        {loan.propertyAddress && (
                          <span>Property: {loan.propertyAddress}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Download Bookmarked Doc button - Requirement 14.1, 14.2, 14.3 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadBookmarkedPdf(loan);
                    }}
                    disabled={
                      loan.status === LoanStatus.CREATED ||
                      loan.status === LoanStatus.PROCESSING ||
                      downloadingBookmarkLoanId === loan.loanId
                    }
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    title={
                      loan.status === LoanStatus.REVIEW_REQUIRED ||
                      loan.status === LoanStatus.REVIEW_COMPLETED
                        ? "Download bookmarked PDF for this loan"
                        : "Available when loan processing is completed"
                    }
                  >
                    {downloadingBookmarkLoanId === loan.loanId ? (
                      <>
                        <Loader className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        Preparing...
                      </>
                    ) : (
                      <>
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download Bookmarked Doc
                      </>
                    )}
                  </button>
                </div>

                {/* Loan Body */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-6">
                    {/* Upload Area - available before, during, and after processing */}
                    {canManageDocs && (
                      <div className="mb-6">
                        {!isStepNotStarted && (
                          <p className="mb-2 text-xs text-gray-500">
                            Add more documents to this loan. Newly uploaded files
                            are submitted for processing with the "Submit Added
                            Documents" button below.
                          </p>
                        )}
                        <div
                          className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 ${
                            isDragOver === loan.loanId
                              ? "border-blue-400 bg-blue-100"
                              : "border-gray-300 hover:border-gray-400 bg-gray-50"
                          }`}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOver(loan.loanId);
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault();
                            setIsDragOver(null);
                          }}
                          onDrop={(e) => handleDrop(e, loan.loanId)}
                        >
                          <div className="text-center">
                            <Upload className="mx-auto h-10 w-10 text-gray-400" />
                            <label
                              htmlFor={`file-upload-${loan.loanId}`}
                              className="cursor-pointer"
                            >
                              <span className="mt-2 block text-sm font-medium text-gray-900 hover:text-blue-500 transition-colors">
                                Drop files here or click to upload
                              </span>
                              <input
                                id={`file-upload-${loan.loanId}`}
                                type="file"
                                className="sr-only"
                                multiple
                                accept={ACCEPTED_FILE_TYPES}
                                onChange={(e) =>
                                  handleFileSelect(e, loan.loanId)
                                }
                              />
                            </label>
                            <p className="mt-1 text-xs text-gray-500">
                              {SUPPORTED_FILE_TYPES_DISPLAY}
                            </p>
                          </div>
                        </div>

                        {/* Upload File List */}
                        {uploads.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {uploads.map((file) => (
                              <div
                                key={file.id}
                                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50"
                              >
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                  <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {file.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(file.size)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-3 ml-3">
                                  {file.status === "uploading" && (
                                    <div className="flex items-center space-x-2">
                                      <div className="w-20 bg-gray-200 rounded-full h-1.5">
                                        <div
                                          className="bg-blue-500 h-1.5 rounded-full transition-all"
                                          style={{ width: `${file.progress}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-gray-500">
                                        {file.progress}%
                                      </span>
                                    </div>
                                  )}
                                  {file.status === "completed" && (
                                    <span className="flex items-center text-xs text-green-600">
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Uploaded
                                    </span>
                                  )}
                                  {file.status === "error" && (
                                    <span className="flex items-center text-xs text-red-600">
                                      <AlertCircle className="h-4 w-4 mr-1" />
                                      {file.error || "Failed"}
                                    </span>
                                  )}
                                  {file.status === "rejected" && (
                                    <span className="flex items-center text-xs text-orange-600">
                                      <FileWarning className="h-4 w-4 mr-1" />
                                      {file.error || "Unsupported format"}
                                    </span>
                                  )}
                                  <button
                                    onClick={() =>
                                      removeFile(loan.loanId, file.id)
                                    }
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Processing Skeleton */}
                    {loan.status === LoanStatus.PROCESSING &&
                      loanFilesData.length === 0 && (
                        <div className="mb-4 space-y-2">
                          <div className="flex items-center space-x-2 mb-3">
                            <Loader className="h-4 w-4 text-blue-400 animate-spin" />
                            <span className="text-sm text-blue-400">
                              Extracting documents...
                            </span>
                          </div>
                          {[1, 2, 3].map((i) => (
                            <div
                              key={i}
                              className="border border-gray-200 rounded-lg bg-gray-50 p-4 animate-pulse"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="h-5 w-5 rounded bg-gray-200 flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                  <div className="h-3 bg-gray-200 rounded w-2/5" />
                                  <div className="h-2 bg-gray-200 rounded w-1/4" />
                                </div>
                                <div className="h-5 w-16 bg-blue-200 rounded" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    {/* Processed Files */}
                    {loanFilesData.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">
                          Processing Files ({loanFilesData.length})
                        </h4>
                        <div className="space-y-3">
                          {loanFilesData.map((file) => {
                            const isFileExpanded = expandedFiles.has(
                              file.fileId,
                            );
                            const documents =
                              fileDocuments.get(file.fileId) || [];
                            return (
                              <div
                                key={file.fileId}
                                className="border border-gray-200 rounded-lg bg-gray-50"
                              >
                                <div
                                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                                  onClick={() =>
                                    toggleFileExpansion(file.fileId)
                                  }
                                >
                                  <div className="flex items-center space-x-3 flex-1">
                                    {isFileExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-gray-500" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-gray-500" />
                                    )}
                                    <FileText className="h-5 w-5 text-gray-400" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.filename}
                                      </p>
                                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                                        {file.pageCount && (
                                          <span>{file.pageCount} pages</span>
                                        )}
                                        {file.splitDocumentCount > 0 && (
                                          <span>
                                            {file.splitDocumentCount} docs
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <span
                                      className={getFileStatusBadge(
                                        file.status,
                                      )}
                                    >
                                      {formatStatus(file.status)}
                                    </span>
                                    {/* Title Search reviews in the Workspace Review mode (onReviewFile), never on the legacy page. */}
                                    {(jobType !== JobType.TR || onReviewFile) &&
                                      file.status ===
                                      FileStatus.REVIEW_REQUIRED &&
                                      (fileDocuments.get(file.fileId) || [])
                                        .length > 0 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleReviewFile(file);
                                          }}
                                          disabled={
                                            reviewingFileId === file.fileId
                                          }
                                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                          style={{ backgroundColor: "#3730A3" }}
                                          title="Review extracted data for this file"
                                        >
                                          {reviewingFileId === file.fileId ? (
                                            <>
                                              <Loader className="h-3 w-3 mr-1 animate-spin" />
                                              Starting...
                                            </>
                                          ) : (
                                            <>
                                              <ClipboardCheck className="h-3 w-3 mr-1" />
                                              Review data
                                            </>
                                          )}
                                        </button>
                                      )}
                                    {canManageDocs && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setConfirmDeleteFile({
                                            file,
                                            loanId: loan.loanId,
                                          });
                                        }}
                                        disabled={deletingFileId === file.fileId}
                                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        title="Delete this file"
                                      >
                                        {deletingFileId === file.fileId ? (
                                          <Loader className="h-3 w-3 mr-1 animate-spin" />
                                        ) : (
                                          <Trash2 className="h-3 w-3 mr-1" />
                                        )}
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {isFileExpanded && documents.length > 0 && (
                                  <div className="border-t border-gray-200 p-4 overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead>
                                        <tr>
                                          {[
                                            "Document ID",
                                            "Pages",
                                            "Document Class",
                                            "Confidence",
                                            "Extracted Fields",
                                            "Actions",
                                          ].map((h) => (
                                            <th
                                              key={h}
                                              className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                            >
                                              {h}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {[...documents].sort((a, b) => {
                                          const aStart = parseInt(a.pageRange?.split('-')[0] || '1', 10);
                                          const bStart = parseInt(b.pageRange?.split('-')[0] || '1', 10);
                                          return aStart - bStart;
                                        }).map((doc) => {
                                          const needsAttention = doc.confidenceScore != null && doc.confidenceScore < 0.8;
                                          const parsedFields: Array<{field: string; value: string; confidence: number}> = (() => {
                                            try {
                                              const raw = doc.extractedFields
                                                ? JSON.parse(
                                                    doc.extractedFields,
                                                  )
                                                : [];
                                              return raw
                                                .filter(
                                                  (f: any) =>
                                                    String(f?.action ?? "").toUpperCase() !==
                                                    "REJECT",
                                                )
                                                .map((f: any) => ({
                                                field:
                                                  f.field_name ?? f.field ?? "",
                                                value:
                                                  f.field_value ??
                                                  f.value ??
                                                  "",
                                                confidence:
                                                  f.confidence_score ??
                                                  f.confidence ??
                                                  0,
                                              }));
                                            } catch {
                                              return [];
                                            }
                                          })();
                                          const isDocExpanded =
                                            expandedFiles.has(
                                              `doc-${doc.documentId}`,
                                            );
                                          return (
                                            <React.Fragment
                                              key={doc.documentId}
                                            >
                                              <tr
                                                className={
                                                  needsAttention
                                                    ? "bg-yellow-50/50"
                                                    : "hover:bg-gray-50"
                                                }
                                              >
                                                <td className="px-3 py-3 text-sm">
                                                  <div className="flex items-center space-x-2">
                                                    <span className="font-mono text-xs text-gray-600">
                                                      {doc.documentId.substring(
                                                        0,
                                                        8,
                                                      )}
                                                      ...
                                                    </span>
                                                    {needsAttention && (
                                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">
                                                        <AlertCircle className="h-3 w-3 mr-1" />
                                                        Needs Attention
                                                      </span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td className="px-3 py-3 text-sm text-gray-900">
                                                  {doc.pageRange}
                                                </td>
                                                <td className="px-3 py-3 text-sm text-gray-900">
                                                  <div className="max-w-xs truncate">
                                                    {doc.documentType || "-"}
                                                  </div>
                                                </td>
                                                <td className="px-3 py-3 text-sm">
                                                  {doc.confidenceScore !=
                                                  null ? (
                                                    <span
                                                      className={
                                                        doc.confidenceScore >=
                                                        0.8
                                                          ? "text-green-600 font-medium"
                                                          : "text-yellow-600 font-medium"
                                                      }
                                                    >
                                                      {(
                                                        doc.confidenceScore *
                                                        100
                                                      ).toFixed(1)}
                                                      %
                                                    </span>
                                                  ) : (
                                                    <span className="text-gray-400">
                                                      -
                                                    </span>
                                                  )}
                                                </td>
                                                <td
                                                  className="px-3 py-3"
                                                  data-testid="extracted-fields-cell"
                                                >
                                                  {parsedFields.length > 0 ? (
                                                    <button
                                                      onClick={() =>
                                                        toggleFileExpansion(
                                                          `doc-${doc.documentId}`,
                                                        )
                                                      }
                                                      className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                                                    >
                                                      {isDocExpanded ? (
                                                        <ChevronDown className="h-4 w-4 mr-1" />
                                                      ) : (
                                                        <ChevronRight className="h-4 w-4 mr-1" />
                                                      )}
                                                      Fields (
                                                      {parsedFields.length})
                                                    </button>
                                                  ) : (
                                                    <span className="text-xs text-gray-400 italic">
                                                      No data extracted
                                                    </span>
                                                  )}
                                                </td>
                                                <td
                                                  className="px-3 py-3 relative z-10"
                                                  data-testid="actions-cell"
                                                >
                                                  <div className="flex items-center space-x-2">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenDocument(doc);
                                                      }}
                                                      disabled={
                                                        openingDocId ===
                                                        doc.documentId
                                                      }
                                                      className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                      title="Open in new tab"
                                                    >
                                                      {openingDocId ===
                                                      doc.documentId ? (
                                                        <>
                                                          <Loader className="h-3 w-3 mr-1 animate-spin" />
                                                          Opening...
                                                        </>
                                                      ) : (
                                                        <>
                                                          <ExternalLink className="h-3 w-3 mr-1" />
                                                          Open
                                                        </>
                                                      )}
                                                    </button>
                                                    {doc.reviewTaskARN && (
                                                      <button
                                                        onClick={() =>
                                                          handleOpenA2IReview(
                                                            doc.reviewTaskARN!,
                                                          )
                                                        }
                                                        className="inline-flex items-center text-xs text-yellow-600 hover:text-yellow-800 transition-colors"
                                                      >
                                                        <ExternalLink className="h-4 w-4 mr-1" />
                                                        Review
                                                      </button>
                                                    )}
                                                    {canManageDocs && (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setConfirmDeleteDoc({
                                                            doc,
                                                            fileId: file.fileId,
                                                            loanId: loan.loanId,
                                                          });
                                                        }}
                                                        disabled={
                                                          deletingDocId ===
                                                          doc.documentId
                                                        }
                                                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        title="Delete this document"
                                                      >
                                                        {deletingDocId ===
                                                        doc.documentId ? (
                                                          <Loader className="h-3 w-3 mr-1 animate-spin" />
                                                        ) : (
                                                          <Trash2 className="h-3 w-3 mr-1" />
                                                        )}
                                                        Delete
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                              {isDocExpanded &&
                                                parsedFields.length > 0 && (
                                                  <tr>
                                                    <td
                                                      colSpan={6}
                                                      className="px-3 pt-0 pb-3"
                                                    >
                                                      <div className="mb-3 rounded-lg border border-gray-200 overflow-hidden">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                          <thead className="bg-gray-100">
                                                            <tr>
                                                              {[
                                                                "Field",
                                                                "Value",
                                                                "Confidence",
                                                              ].map((h) => (
                                                                <th
                                                                  key={h}
                                                                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                                                >
                                                                  {h}
                                                                </th>
                                                              ))}
                                                            </tr>
                                                          </thead>
                                                          <tbody className="divide-y divide-gray-100 bg-white">
                                                            {parsedFields.map(
                                                              (f, i) => (
                                                                <tr
                                                                  key={i}
                                                                  className="hover:bg-gray-50"
                                                                >
                                                                  <td className="px-3 py-2 text-xs font-medium text-gray-700 whitespace-nowrap">
                                                                    {f.field}
                                                                  </td>
                                                                  <td className="px-3 py-2 text-xs text-gray-900 max-w-xs">
                                                                    {f.value || (
                                                                      <span className="text-gray-400 italic">
                                                                        empty
                                                                      </span>
                                                                    )}
                                                                  </td>
                                                                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                                                                    {f.confidence >
                                                                    0 ? (
                                                                      <span
                                                                        className={
                                                                          f.confidence >=
                                                                          0.8
                                                                            ? "text-green-600 font-medium"
                                                                            : "text-yellow-600 font-medium"
                                                                        }
                                                                      >
                                                                        {(
                                                                          f.confidence *
                                                                          100
                                                                        ).toFixed(
                                                                          1,
                                                                        )}
                                                                        %
                                                                      </span>
                                                                    ) : (
                                                                      <span className="text-gray-400">
                                                                        -
                                                                      </span>
                                                                    )}
                                                                  </td>
                                                                </tr>
                                                              ),
                                                            )}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Submit Job Button */}
      {loans.length > 0 && isStepNotStarted && (
        <div className="flex items-center justify-end space-x-3">
          {!canSubmit && loans.length > 0 && (
            <p className="text-sm text-yellow-600">
              All loans must have at least one uploaded file before submitting.
            </p>
          )}
          <button
            onClick={handleSubmitJob}
            disabled={!canSubmit}
            title={
              !canSubmit
                ? "All loans must have at least one uploaded file"
                : undefined
            }
            className={`inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white transition-all ${
              canSubmit
                ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                : "bg-gray-400 cursor-not-allowed opacity-60"
            }`}
          >
            {submitting ? (
              <>
                <Loader className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit for Processing
              </>
            )}
          </button>
        </div>
      )}

      {/* Submit Added Documents — when more files were uploaded during/after processing */}
      {(isStepInProgress || isStepCompleted) && hasPendingUploads && (
        <div className="flex items-center justify-end space-x-3">
          <p className="text-sm text-gray-600">
            {anyUploading
              ? "Some documents are still uploading. Wait until uploads finish before submitting."
              : "New documents uploaded. Submit them to process this job."}
          </p>
          <button
            onClick={handleProcessAdditionalDocuments}
            disabled={!canSubmitAdditional}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {processingAdditional ? (
              <>
                <Loader className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Added Documents
              </>
            )}
          </button>
        </div>
      )}

      {/* Add Loans Modal */}
      {addLoansModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            {addLoansModal.step === "count" && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Add Loans
                  </h2>
                  <button
                    onClick={() => setAddLoansModal(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number of loans to add
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={addLoansModal.count}
                      onChange={(e) =>
                        setAddLoansModal({
                          ...addLoansModal,
                          count: Math.max(
                            1,
                            Math.min(100, parseInt(e.target.value) || 1),
                          ),
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      id="auto-generate"
                      type="checkbox"
                      checked={addLoansModal.autoGenerate}
                      onChange={(e) =>
                        setAddLoansModal({
                          ...addLoansModal,
                          autoGenerate: e.target.checked,
                        })
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="auto-generate"
                      className="text-sm text-gray-700"
                    >
                      Auto-generate loan IDs
                    </label>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setAddLoansModal(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddLoansCountSubmit}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all"
                  >
                    {addLoansModal.autoGenerate
                      ? "Add Loans"
                      : "Next: Enter IDs"}
                  </button>
                </div>
              </>
            )}

            {addLoansModal.step === "ids" && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Enter Loan IDs
                  </h2>
                  <button
                    onClick={() => setAddLoansModal(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {addLoansModal.loanIds.map((id, idx) => (
                    <div key={idx}>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Loan {idx + 1}
                      </label>
                      <input
                        type="text"
                        value={id}
                        onChange={(e) => {
                          const updated = [...addLoansModal.loanIds];
                          updated[idx] = e.target.value;
                          setAddLoansModal({
                            ...addLoansModal,
                            loanIds: updated,
                          });
                        }}
                        placeholder={`e.g., LN-2024-00${idx + 1}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() =>
                      setAddLoansModal({ ...addLoansModal, step: "count" })
                    }
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
                  >
                    Back
                  </button>
                  <button
                    onClick={() =>
                      handleConfirmAddLoans(
                        addLoansModal.count,
                        false,
                        addLoansModal.loanIds,
                      )
                    }
                    disabled={addLoansModal.loanIds.some((id) => !id.trim())}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Add Loans
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Loan Modal */}
      {editingLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                Edit Loan Information
              </h2>
              <button
                onClick={() => setEditingLoan(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              {[
                {
                  id: "loanNumber",
                  label: "Loan ID / Number",
                  value: editLoanNumber,
                  setter: setEditLoanNumber,
                  placeholder: "e.g., LN-2024-001",
                },
                {
                  id: "borrowerName",
                  label: "Borrower Name",
                  value: editBorrowerName,
                  setter: setEditBorrowerName,
                  placeholder: "e.g., John Doe",
                },
                {
                  id: "propertyAddress",
                  label: "Property Address",
                  value: editPropertyAddress,
                  setter: setEditPropertyAddress,
                  placeholder: "e.g., 123 Main St",
                },
              ].map(({ id, label, value, setter, placeholder }) => (
                <div key={id}>
                  <label
                    htmlFor={id}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {label}
                  </label>
                  <input
                    id={id}
                    type="text"
                    value={value}
                    onChange={(e) => setter(e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setEditingLoan(null)}
                disabled={savingLoanInfo}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLoanInfo}
                disabled={savingLoanInfo}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all"
              >
                {savingLoanInfo ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Resubmit Modal */}
      {confirmResubmit && (
        <ConfirmModal
          title="Retry Processing"
          message={`Retry processing for "${job.name}"? This will reprocess only the failed documents.`}
          confirmLabel="Retry"
          variant="danger"
          onConfirm={handleResubmitConfirm}
          onCancel={() => setConfirmResubmit(false)}
        />
      )}

      {/* Confirm Delete File Modal */}
      {confirmDeleteFile && (
        <ConfirmModal
          title="Delete File"
          message={`Delete "${confirmDeleteFile.file.filename}" and all of its extracted documents? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleConfirmDeleteFile}
          onCancel={() => setConfirmDeleteFile(null)}
        />
      )}

      {/* Confirm Delete Document Modal */}
      {confirmDeleteDoc && (
        <ConfirmModal
          title="Delete Document"
          message={`Delete document "${confirmDeleteDoc.doc.documentType || confirmDeleteDoc.doc.documentId.substring(0, 8)}" (pages ${confirmDeleteDoc.doc.pageRange})? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleConfirmDeleteDocument}
          onCancel={() => setConfirmDeleteDoc(null)}
        />
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal(null)}
        />
      )}

      {/* Next: Document Verification button — shown when document processing is complete */}
      {isStepCompleted && jobType !== JobType.TR && (
        <div className="flex justify-end mt-4">
          <button
            onClick={async () => {
              try {
                const session = await fetchAuthSession();
                const tenantId =
                  (session.tokens?.idToken?.payload[
                    "custom:tenantId"
                  ] as string) || "default-tenant";
                await client.graphql({
                  query: mutations.completeStep,
                  variables: {
                    jobId: job.jobId,
                    tenantId,
                    step: "DOCUMENT_PROCESSING",
                  },
                });
              } catch (err) {
                console.error("Failed to update job status:", err);
              }
              onStepComplete();
            }}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all"
          >
            Next: Document Verification
            <ChevronRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}
      {/* Filename Conflict Modal */}
      {conflictModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                File Already Exists
              </h3>
              <button
                onClick={() => setConflictModal(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              The following file
              {conflictModal.conflictingFiles.length !== 1 ? "s" : ""} already{" "}
              {conflictModal.conflictingFiles.length !== 1 ? "exist" : "exists"}{" "}
              in this loan:
            </p>
            <ul className="mb-4 space-y-2">
              {conflictModal.conflictingFiles.map(({ file, proposedName }) => (
                <li
                  key={`${file.name}-${proposedName}`}
                  className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-gray-50"
                >
                  <span className="font-medium text-gray-900 truncate">
                    {file.name}
                  </span>
                  <span className="text-gray-400 flex-shrink-0">→</span>
                  <span className="text-blue-600 truncate flex-shrink-0">
                    {proposedName}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    (if new)
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-600 mb-6">
              Do you want to replace the existing file or upload as a new file?
            </p>
            <div className="flex justify-end gap-3 flex-wrap">
              <button
                onClick={() => setConflictModal(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConflictReplace}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Replace Existing
              </button>
              <button
                onClick={handleConflictUploadNew}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Upload as New
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentProcessingStep;
